import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Clock,
  MapPin,
  Truck,
} from 'lucide-react';
import {
  homeScrollItem,
  homeScrollStagger,
} from '../../utils/homeScrollMotion';
import styles from './ServiceHero.module.css';

const TRUST = [
  { icon: MapPin, text: 'Meerut facility' },
  { icon: Truck, text: 'Pan-India dispatch' },
  { icon: Clock, text: '1-day response' },
];

const ServiceHero = ({
  icon: Icon,
  hue = 'green',
  hero,
  title,
  headline,
  summary,
  highlights = [],
}) => {
  const features = hero?.features?.length ? hero.features : highlights.slice(0, 4);
  const displayTitle = hero?.boldTitle ?? title;
  const titleWords = displayTitle?.trim().split(/\s+/) ?? [];
  const titleLead = titleWords.slice(0, -1).join(' ');
  const titleAccent = titleWords.length > 1 ? titleWords[titleWords.length - 1] : null;

  return (
    <section
      className={`${styles.hero} home-bg-linen`}
      data-hue={hue}
      aria-labelledby="service-hero-title"
    >
      <div className={styles.backdrop} aria-hidden="true">
        <span className={styles.orbGreen} />
        <span className={styles.orbOrange} />
        <span className={styles.mesh} />
      </div>

      <div className={styles.inner}>
        <Link to="/services" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden="true" />
          All services
        </Link>

        <motion.article
          className={styles.shell}
          variants={homeScrollItem}
          initial="hidden"
          animate="visible"
        >
          <span className={styles.shellBar} aria-hidden="true" />

          <div className={styles.grid}>
            <div className={styles.copy}>
              <div className={styles.meta}>
                <span className={styles.iconWrap} aria-hidden="true">
                  <Icon size={20} strokeWidth={2.2} />
                </span>
                <div className={styles.metaText}>
                  {hero?.badge && <span className={styles.badge}>{hero.badge}</span>}
                  {hero?.scriptTitle && (
                    <span className={styles.category}>{hero.scriptTitle}</span>
                  )}
                </div>
              </div>

              <h1 id="service-hero-title" className={styles.title}>
                {titleAccent ? (
                  <>
                    {titleLead}{' '}
                    <span className={styles.titleAccent}>{titleAccent}</span>
                  </>
                ) : (
                  displayTitle
                )}
              </h1>

              <div className={styles.rule} aria-hidden="true">
                <span />
                <span />
              </div>

              {headline && <p className={styles.headline}>{headline}</p>}
              {summary && <p className={styles.summary}>{summary}</p>}

              <Link to="/contact" className={styles.cta}>
                Talk to our team
                <ArrowUpRight size={16} strokeWidth={2.5} aria-hidden="true" />
              </Link>

              <ul className={styles.trust}>
                {TRUST.map(({ icon: TrustIcon, text }) => (
                  <li key={text}>
                    <TrustIcon size={13} strokeWidth={2.3} aria-hidden="true" />
                    {text}
                  </li>
                ))}
              </ul>
            </div>

            <motion.aside
              className={styles.showcase}
              aria-label="What's included"
              variants={homeScrollStagger}
              initial="hidden"
              animate="visible"
            >
              <div className={styles.panelHead}>
                <span className={styles.panelPattern} aria-hidden="true" />
                <div className={styles.iconStage} aria-hidden="true">
                  <span className={styles.ringOuter} />
                  <span className={styles.ringInner} />
                  <span className={styles.iconHero}>
                    <Icon size={28} strokeWidth={2} />
                  </span>
                </div>
                <div className={styles.panelIntro}>
                  <h2 className={styles.panelTitle}>What&apos;s included</h2>
                  <p className={styles.panelSub}>
                    {hero?.scriptTitle ?? 'Everything your line needs from Greeneri'}
                  </p>
                </div>
              </div>

              {features.length > 0 && (
                <ul className={styles.features}>
                  {features.map((point) => (
                    <motion.li
                      key={point}
                      className={styles.feature}
                      variants={homeScrollItem}
                    >
                      <span className={styles.checkWrap} aria-hidden="true">
                        <Check size={16} strokeWidth={2.8} />
                      </span>
                      <span className={styles.featureText}>{point}</span>
                    </motion.li>
                  ))}
                </ul>
              )}
            </motion.aside>
          </div>
        </motion.article>
      </div>
    </section>
  );
};

export default ServiceHero;
